/*
  Presentation of values that carry meaning: money, dates, durations, counts.

  Money is never reformatted from a float. The API sends exact decimals as
  numbers with at most two places, and these helpers only choose how to show
  them — they never round a value the business relies on.
*/

const locale = () => document.documentElement.lang || "en-GB";

export function money(
  amount: number | null | undefined,
  currency: string | null | undefined = "USD",
  options: { compact?: boolean; decimals?: boolean } = {},
): string {
  if (amount === null || amount === undefined) return "—";

  const { compact = false, decimals = true } = options;

  return new Intl.NumberFormat(locale(), {
    style: "currency",
    currency: currency || "USD",
    notation: compact && Math.abs(amount) >= 10_000 ? "compact" : "standard",
    maximumFractionDigits: compact && Math.abs(amount) >= 10_000 ? 1 : decimals ? 2 : 0,
    minimumFractionDigits: compact || !decimals ? 0 : 2,
  }).format(amount);
}

export function number(value: number | null | undefined, decimals = 0): string {
  if (value === null || value === undefined) return "—";

  return new Intl.NumberFormat(locale(), {
    maximumFractionDigits: decimals,
    minimumFractionDigits: decimals,
  }).format(value);
}

export function compactNumber(value: number | null | undefined): string {
  if (value === null || value === undefined) return "—";

  return new Intl.NumberFormat(locale(), {
    notation: Math.abs(value) >= 10_000 ? "compact" : "standard",
    maximumFractionDigits: 1,
  }).format(value);
}

export function percent(value: number | null | undefined, decimals = 0): string {
  if (value === null || value === undefined) return "—";
  return `${number(value, decimals)}%`;
}

/**
 * A rate the API holds as a fraction — VAT, commission, discount — shown as a
 * percentage: 0.15 becomes "15%" and 0.125 becomes "12.5%". Passing a fraction to
 * percent() would print "0%".
 */
export function ratePercent(rate: number | null | undefined): string {
  if (rate === null || rate === undefined) return "—";

  return `${new Intl.NumberFormat(locale(), { maximumFractionDigits: 2 }).format(rate * 100)}%`;
}

/** Audit entries hold the changed field names as one comma-separated value. */
export function changedFieldList(value: string | null | undefined): string[] {
  return value ? value.split(",").map((field) => field.trim()).filter(Boolean) : [];
}

/** A date with no time component (an event day, an invoice date). */
export function date(value: string | null | undefined): string {
  if (!value) return "—";

  const parsed = new Date(value.length <= 10 ? `${value}T00:00:00` : value);
  if (Number.isNaN(parsed.valueOf())) return "—";

  return parsed.toLocaleDateString(locale(), {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

export function dateShort(value: string | null | undefined): string {
  if (!value) return "—";

  const parsed = new Date(value.length <= 10 ? `${value}T00:00:00` : value);
  if (Number.isNaN(parsed.valueOf())) return "—";

  return parsed.toLocaleDateString(locale(), { day: "numeric", month: "short" });
}

/** A precise instant, shown in the viewer's own time zone. */
export function dateTime(value: string | null | undefined): string {
  if (!value) return "—";

  const parsed = new Date(value);
  if (Number.isNaN(parsed.valueOf())) return "—";

  return parsed.toLocaleString(locale(), {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function time(value: string | null | undefined): string {
  if (!value) return "—";

  const parsed = new Date(value);
  if (Number.isNaN(parsed.valueOf())) return "—";

  return parsed.toLocaleTimeString(locale(), { hour: "2-digit", minute: "2-digit" });
}

/** A date range written the way people say it: "10 – 12 Mar 2027". */
export function dateRange(
  from: string | null | undefined,
  to: string | null | undefined,
): string {
  if (!from && !to) return "—";
  if (!to) return date(from);
  if (!from) return date(to);

  const a = new Date(from.length <= 10 ? `${from}T00:00:00` : from);
  const b = new Date(to.length <= 10 ? `${to}T00:00:00` : to);

  if (Number.isNaN(a.valueOf()) || Number.isNaN(b.valueOf())) return "—";

  if (a > b) return `${date(from)} – ${date(to)}`;

  // The locale decides the order and which parts are shared: "Jul 6 – 7, 2027" in en-US and
  // "6–7 Jul 2027" in en-GB. Joining a bare start day onto a fully formatted end date dropped the
  // start month wherever the month is written before the day.
  return new Intl.DateTimeFormat(locale(), { day: "numeric", month: "short", year: "numeric" }).formatRange(a, b);
}

/** "2 days ago", "in 5 days" — relative to now, for deadlines and activity. */
export function relative(value: string | null | undefined): string {
  if (!value) return "—";

  const parsed = new Date(value.length <= 10 ? `${value}T00:00:00` : value);
  if (Number.isNaN(parsed.valueOf())) return "—";

  const diffMs = parsed.valueOf() - Date.now();
  const abs = Math.abs(diffMs);
  const formatter = new Intl.RelativeTimeFormat(locale(), { numeric: "auto" });

  const minute = 60_000;
  const hour = 60 * minute;
  const day = 24 * hour;

  if (abs < minute) return "just now";
  if (abs < hour) return formatter.format(Math.round(diffMs / minute), "minute");
  if (abs < day) return formatter.format(Math.round(diffMs / hour), "hour");
  if (abs < 30 * day) return formatter.format(Math.round(diffMs / day), "day");
  if (abs < 365 * day) return formatter.format(Math.round(diffMs / (30 * day)), "month");

  return formatter.format(Math.round(diffMs / (365 * day)), "year");
}

/** Whole days from today; negative when the date has passed. */
export function daysUntil(value: string | null | undefined): number | null {
  if (!value) return null;

  const parsed = new Date(value.length <= 10 ? `${value}T00:00:00` : value);
  if (Number.isNaN(parsed.valueOf())) return null;

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  parsed.setHours(0, 0, 0, 0);

  return Math.round((parsed.valueOf() - today.valueOf()) / 86_400_000);
}

export function duration(minutes: number | null | undefined): string {
  if (minutes === null || minutes === undefined) return "—";
  if (minutes < 60) return `${minutes} min`;

  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;

  return rest === 0 ? `${hours} hr` : `${hours} hr ${rest} min`;
}

export function fileSize(bytes: number | null | undefined): string {
  if (bytes === null || bytes === undefined) return "—";
  if (bytes < 1024) return `${bytes} B`;

  const units = ["KB", "MB", "GB", "TB"];
  let value = bytes / 1024;
  let unit = 0;

  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }

  return `${value.toFixed(value < 10 ? 1 : 0)} ${units[unit]}`;
}

/** Turns "RegistrationOpen" into "Registration open" for display. */
export function humanise(value: string | null | undefined): string {
  if (!value) return "—";

  return value
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .replace(/^./, (c) => c.toUpperCase());
}

export function initials(name: string | null | undefined): string {
  if (!name) return "?";

  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();

  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/**
 * True when a deadline has already passed.
 *
 * Kept here rather than inline in a component so "what counts as late" is
 * decided in one place, and so a render is not reading the clock directly.
 */
export function isOverdue(value: string | null | undefined): boolean {
  if (!value) return false;

  const parsed = new Date(value.length <= 10 ? `${value}T23:59:59` : value);
  return !Number.isNaN(parsed.valueOf()) && parsed.valueOf() < new Date().valueOf();
}
